import { Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { LEAD_STREAM_OPTIONS } from '../../constants/lead-stream.constants';
import {
  LEAD_IMPORT_IGNORE,
  LEAD_IMPORT_NOT_MAPPED,
  type LeadImportArea,
  type LeadImportColumnMapping,
  type LeadImportDuplicateAction,
  type LeadImportExecuteResult,
  type LeadImportFieldDefinition,
  type LeadImportParsedFile,
  type LeadImportPreviewResult,
  type LeadImportProgress,
  type LeadImportRowStatus,
  type LeadImportWizardStep,
} from '../../models/lead-import.model';
import { LeadsService } from '../../services/leads.service';
import { TenantLeadsService } from '../../services/tenant-leads.service';
import {
  AdminButtonComponent,
  AdminIconComponent,
  AdminModalComponent,
  AdminTableComponent,
} from '../../admin';
import {
  adjustImportErrorRowIndices,
  buildAutoColumnMapping,
  buildErrorReportCsv,
  buildErrorReportExcel,
  collectUnknownStreamValues,
  countImportableRows,
  LEAD_IMPORT_BATCH_SIZE,
  LEAD_IMPORT_PREVIEW_PAGE_SIZE,
  mergeImportExecuteResults,
  parseLeadImportFile,
} from '../../utils/lead-import.util';

@Component({
  selector: 'app-lead-import-modal',
  standalone: true,
  imports: [FormsModule, AdminModalComponent, AdminButtonComponent, AdminTableComponent, AdminIconComponent],
  templateUrl: './lead-import-modal.component.html',
  styleUrl: './lead-import-modal.component.scss',
})
export class LeadImportModalComponent implements OnInit {
  private readonly leadsService = inject(LeadsService);
  private readonly tenantLeadsService = inject(TenantLeadsService);

  readonly area = input.required<LeadImportArea>();
  readonly closed = output<void>();
  readonly importCompleted = output<void>();

  readonly step = signal<LeadImportWizardStep>('upload');
  readonly loading = signal(false);
  readonly executing = signal(false);
  readonly error = signal<string | null>(null);
  readonly fieldDefinitions = signal<LeadImportFieldDefinition[]>([]);
  readonly parsedFile = signal<LeadImportParsedFile | null>(null);
  readonly columnMapping = signal<LeadImportColumnMapping>({});
  readonly streamValueMappings = signal<Record<string, string>>({});
  readonly unknownStreamValues = signal<string[]>([]);
  readonly preview = signal<LeadImportPreviewResult | null>(null);
  readonly executeResult = signal<LeadImportExecuteResult | null>(null);
  readonly importProgress = signal<LeadImportProgress>({ processed: 0, total: 0 });
  readonly duplicateAction = signal<LeadImportDuplicateAction | ''>('');
  readonly includeWarnings = signal(false);
  readonly previewFilter = signal<LeadImportRowStatus | 'all'>('all');
  readonly previewPage = signal(1);
  readonly dragActive = signal(false);
  readonly selectedFileName = signal<string | null>(null);

  readonly wizardSteps = [
    { id: 'upload', label: 'Upload' },
    { id: 'mapping', label: 'Map columns' },
    { id: 'settings', label: 'Settings' },
    { id: 'preview', label: 'Review' },
    { id: 'results', label: 'Done' },
  ] as const;

  readonly previewPageSize = LEAD_IMPORT_PREVIEW_PAGE_SIZE;

  readonly streamOptions = LEAD_STREAM_OPTIONS;
  readonly ignoreValue = LEAD_IMPORT_IGNORE;
  readonly notMappedValue = LEAD_IMPORT_NOT_MAPPED;

  readonly mappingOptions = computed(() => {
    const defs = this.fieldDefinitions();
    return [
      { value: LEAD_IMPORT_NOT_MAPPED, label: 'Not Mapped' },
      { value: LEAD_IMPORT_IGNORE, label: 'Ignore Column' },
      ...defs.map((field) => ({ value: field.key, label: field.label })),
    ];
  });

  readonly filteredPreviewRows = computed(() => {
    const result = this.preview();
    if (!result) return [];
    const filter = this.previewFilter();
    if (filter === 'all') return result.rows;
    return result.rows.filter((row) => row.status === filter);
  });

  readonly previewPageCount = computed(() => {
    const total = this.filteredPreviewRows().length;
    return total === 0 ? 1 : Math.ceil(total / this.previewPageSize);
  });

  readonly paginatedPreviewRows = computed(() => {
    const rows = this.filteredPreviewRows();
    const page = Math.min(this.previewPage(), this.previewPageCount());
    const start = (page - 1) * this.previewPageSize;
    return rows.slice(start, start + this.previewPageSize);
  });

  readonly previewPageSummary = computed(() => {
    const total = this.filteredPreviewRows().length;
    if (total === 0) return '0 of 0';
    const page = Math.min(this.previewPage(), this.previewPageCount());
    const start = (page - 1) * this.previewPageSize + 1;
    const end = Math.min(page * this.previewPageSize, total);
    return `${start}–${end} of ${total}`;
  });

  readonly importableCount = computed(() => {
    const result = this.preview();
    if (!result) return 0;
    return countImportableRows(result.aggregates, this.includeWarnings());
  });

  readonly canImport = computed(() => {
    return Boolean(this.duplicateAction()) && this.importableCount() > 0 && !this.executing();
  });

  readonly importProgressPercent = computed(() => {
    const { processed, total } = this.importProgress();
    if (total <= 0) return 0;
    return Math.min(100, Math.round((processed / total) * 100));
  });

  readonly importRemaining = computed(() => {
    const { processed, total } = this.importProgress();
    return Math.max(0, total - processed);
  });

  readonly wizardStepIndex = computed(() => {
    const current = this.step();
    if (current === 'upload') return 0;
    if (current === 'mapping') return 1;
    if (current === 'stream' || current === 'duplicate') return 2;
    if (current === 'preview' || current === 'importing') return 3;
    if (current === 'results') return 4;
    return 0;
  });

  readonly wizardStepStates = computed(() => {
    const current = this.wizardStepIndex();
    return this.wizardSteps.map((step, index) => ({
      ...step,
      number: index + 1,
      status: index < current ? 'done' : index === current ? 'active' : 'upcoming',
    }));
  });

  readonly stepHeading = computed(() => {
    switch (this.step()) {
      case 'upload':
        return {
          title: 'Upload your spreadsheet',
          description: 'Start by choosing an Excel or CSV file. We will guide you through mapping and review before anything is saved.',
        };
      case 'mapping':
        return {
          title: 'Match columns to CRM fields',
          description: 'Each column from your file should map to a lead field. Adjust anything the auto-map got wrong.',
        };
      case 'stream':
        return {
          title: 'Fix unrecognized stream values',
          description: 'Some stream values in your file do not match your configured options. Map them or leave blank.',
        };
      case 'duplicate':
        return {
          title: 'What should happen with duplicates?',
          description: 'If a row has the same phone or email as an existing lead, choose how to handle it.',
        };
      case 'preview':
        return {
          title: 'Review before importing',
          description: 'Check row statuses below. Only valid rows (and optional warnings) will be imported.',
        };
      case 'importing':
        return {
          title: 'Importing your leads',
          description: 'Please keep this window open until the import finishes.',
        };
      case 'results':
        return {
          title: 'Import finished',
          description: 'Here is a summary of what was created, updated, or skipped.',
        };
      default:
        return { title: 'Import leads', description: '' };
    }
  });

  readonly parsedRowCount = computed(() => this.parsedFile()?.rows.length ?? 0);

  ngOnInit(): void {
    this.loadFieldDefinitions();
  }

  onModalClose(): void {
    if (this.executing() || this.step() === 'importing') return;
    this.closed.emit();
  }

  finishImport(): void {
    this.importCompleted.emit();
    this.closed.emit();
  }

  startNewImport(): void {
    this.error.set(null);
    this.parsedFile.set(null);
    this.selectedFileName.set(null);
    this.columnMapping.set({});
    this.streamValueMappings.set({});
    this.unknownStreamValues.set([]);
    this.preview.set(null);
    this.executeResult.set(null);
    this.duplicateAction.set('');
    this.includeWarnings.set(false);
    this.previewFilter.set('all');
    this.previewPage.set(1);
    this.step.set('upload');
  }

  setDuplicateAction(value: LeadImportDuplicateAction): void {
    this.duplicateAction.set(value);
  }

  setIncludeWarnings(value: boolean): void {
    this.includeWarnings.set(value);
  }

  setPreviewFilter(filter: LeadImportRowStatus | 'all'): void {
    this.previewFilter.set(filter);
    this.previewPage.set(1);
  }

  goToPreviewPage(page: number): void {
    const next = Math.min(Math.max(1, page), this.previewPageCount());
    this.previewPage.set(next);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.loading()) this.dragActive.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragActive.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragActive.set(false);
    if (this.loading()) return;
    const file = event.dataTransfer?.files?.[0];
    if (file) this.processFile(file);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) this.processFile(file);
    input.value = '';
  }

  mappingStatus(header: string): 'mapped' | 'ignored' | 'unmapped' {
    const value = this.columnMapping()[header];
    if (!value || value === LEAD_IMPORT_NOT_MAPPED) return 'unmapped';
    if (value === LEAD_IMPORT_IGNORE) return 'ignored';
    return 'mapped';
  }

  statusLabel(status: LeadImportRowStatus): string {
    const labels: Record<LeadImportRowStatus, string> = {
      valid: 'Ready',
      warning: 'Warning',
      error: 'Error',
      duplicate: 'Duplicate',
    };
    return labels[status];
  }

  private processFile(file: File): void {
    this.error.set(null);
    this.loading.set(true);
    this.selectedFileName.set(file.name);
    void parseLeadImportFile(file)
      .then((parsed) => {
        if (parsed.headers.length === 0 || parsed.rows.length === 0) {
          this.error.set('The uploaded file has no data rows.');
          this.selectedFileName.set(null);
          return;
        }
        this.parsedFile.set(parsed);
        const mapping = buildAutoColumnMapping(parsed.headers, this.fieldDefinitions());
        this.columnMapping.set(mapping);
        this.streamValueMappings.set({});
        this.unknownStreamValues.set(
          collectUnknownStreamValues(parsed.rows, mapping, {}, this.streamOptions),
        );
        this.preview.set(null);
        this.executeResult.set(null);
        this.duplicateAction.set('');
        this.includeWarnings.set(false);
        this.step.set('mapping');
      })
      .catch(() => {
        this.error.set('Could not read the uploaded file.');
        this.selectedFileName.set(null);
      })
      .finally(() => this.loading.set(false));
  }

  rerunAutoMap(): void {
    const parsed = this.parsedFile();
    if (!parsed) return;
    const mapping = buildAutoColumnMapping(parsed.headers, this.fieldDefinitions());
    this.columnMapping.set(mapping);
    this.refreshUnknownStreamValues();
  }

  onMappingChange(header: string, value: string): void {
    this.columnMapping.update((current) => ({
      ...current,
      [header]: value as LeadImportColumnMapping[string],
    }));
    this.refreshUnknownStreamValues();
  }

  goToStreamStep(): void {
    this.refreshUnknownStreamValues();
    if (this.unknownStreamValues().length > 0) {
      this.step.set('stream');
      return;
    }
    this.step.set('duplicate');
  }

  goToDuplicateStep(): void {
    this.step.set('duplicate');
  }

  runPreview(): void {
    const parsed = this.parsedFile();
    if (!parsed) return;
    if (!this.duplicateAction()) {
      this.error.set('Select a duplicate handling option before previewing.');
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    const payload = this.buildImportPayload(parsed);

    this.importApi()
      .previewImport(payload)
      .subscribe({
        next: (result) => {
          this.preview.set(result);
          this.previewPage.set(1);
          this.previewFilter.set('all');
          this.step.set('preview');
          this.loading.set(false);
        },
        error: (err) => {
          this.error.set(err?.error?.message ?? 'Preview failed.');
          this.loading.set(false);
        },
      });
  }

  async executeImport(): Promise<void> {
    const parsed = this.parsedFile();
    const action = this.duplicateAction();
    if (!parsed || !action) return;

    const allRows = parsed.rows.map((data) => ({ data }));
    const total = allRows.length;

    this.executing.set(true);
    this.error.set(null);
    this.importProgress.set({ processed: 0, total });
    this.step.set('importing');

    const batchResults: LeadImportExecuteResult[] = [];
    const basePayload = {
      columnMapping: this.columnMapping() as Record<string, string>,
      streamValueMappings: this.streamValueMappings(),
      duplicateAction: action,
      includeWarnings: this.includeWarnings(),
    };

    try {
      for (let start = 0; start < total; start += LEAD_IMPORT_BATCH_SIZE) {
        const batchRows = allRows.slice(start, start + LEAD_IMPORT_BATCH_SIZE);
        const result = await firstValueFrom(
          this.importApi().executeImport({
            ...basePayload,
            rows: batchRows,
          }),
        );

        batchResults.push({
          ...result,
          errors: adjustImportErrorRowIndices(result.errors, start),
        });

        this.importProgress.set({
          processed: Math.min(start + batchRows.length, total),
          total,
        });
      }

      this.executeResult.set(mergeImportExecuteResults(batchResults));
      this.step.set('results');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'error' in err
          ? (err as { error?: { message?: string } }).error?.message
          : undefined;
      this.error.set(message ?? 'Import failed.');
      this.step.set('preview');
    } finally {
      this.executing.set(false);
      this.importProgress.set({ processed: 0, total: 0 });
    }
  }

  downloadErrorReport(format: 'xlsx' | 'csv'): void {
    const result = this.executeResult();
    if (!result?.errors.length) return;
    const stamp = new Date().toISOString().slice(0, 10);
    if (format === 'xlsx') {
      buildErrorReportExcel(result.errors, `lead-import-errors-${stamp}.xlsx`);
      return;
    }
    buildErrorReportCsv(result.errors, `lead-import-errors-${stamp}.csv`);
  }

  sampleValues(header: string): string {
    const parsed = this.parsedFile();
    if (!parsed) return '';
    return parsed.sampleRows
      .map((row) => row[header])
      .filter((value) => value?.trim())
      .slice(0, 3)
      .join(' · ');
  }

  setStreamMapping(rawValue: string, mapped: string): void {
    this.streamValueMappings.update((current) => {
      const next = { ...current };
      if (!mapped) {
        delete next[rawValue];
      } else {
        next[rawValue] = mapped;
      }
      return next;
    });
  }

  streamMappingFor(rawValue: string): string {
    return this.streamValueMappings()[rawValue] || '';
  }

  private loadFieldDefinitions(): void {
    this.importApi()
      .getImportFieldDefinitions()
      .subscribe({
        next: (defs) => this.fieldDefinitions.set(defs),
        error: () => this.error.set('Could not load import field definitions.'),
      });
  }

  private importApi() {
    return this.area() === 'tenant' ? this.tenantLeadsService : this.leadsService;
  }

  private buildImportPayload(parsed: LeadImportParsedFile) {
    return {
      columnMapping: this.columnMapping() as Record<string, string>,
      rows: parsed.rows.map((data) => ({ data })),
      streamValueMappings: this.streamValueMappings(),
    };
  }

  private refreshUnknownStreamValues(): void {
    const parsed = this.parsedFile();
    if (!parsed) return;
    this.unknownStreamValues.set(
      collectUnknownStreamValues(
        parsed.rows,
        this.columnMapping(),
        this.streamValueMappings(),
        this.streamOptions,
      ),
    );
  }
}
